import { PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { useAppSelector } from '~/hooks/useAppSelector';
import { RemoteConfigUtils } from '~/utils/RemoteConfigUtils';
import { OfficeTutorialContext } from './Context';
import { OfficeTutorialPopup } from './OfficeTutorialPopup';
import { SlideDirection, TOfficeTutorial } from './Types';

export const OfficeTutorialPopupProvider = ({ children }: PropsWithChildren) => {
  const [tutorial, setTutorial] = useState<TOfficeTutorial>();
  const [[index, direction], setIndex] = useState<[number, SlideDirection]>([
    -1,
    SlideDirection.Initial,
  ]);

  const isParentAccount = useAppSelector((state) => state.user?.childOffices?.length > 1);

  const tutorialSteps = useMemo(() => {
    if (isParentAccount) {
      return tutorial?.parentImages;
    }
    return tutorial?.childImages;
  }, [isParentAccount, tutorial?.childImages, tutorial?.parentImages]);

  const isLastSlide = tutorialSteps && index === tutorialSteps!.images.length - 1;

  const onPrevSlide = useCallback(() => {
    setIndex(([i]) => [i - 1, SlideDirection.Prev]);
  }, []);

  const onClose = useCallback(() => {
    setTutorial(undefined);
    setIndex([-1, SlideDirection.Initial]);
  }, []);

  const onNextSlide = useCallback(() => {
    if (isLastSlide) {
      onClose();
      return;
    }

    setIndex(([i]) => [i + 1, SlideDirection.Next]);
  }, [isLastSlide, onClose]);

  const openTutorial = useCallback((tutorialId: string) => {
    const tutorial = RemoteConfigUtils.getOfficeTutorials().find(
      (it: TOfficeTutorial) => it.id === tutorialId,
    );
    if (tutorial) {
      setTutorial(tutorial);
    }
  }, []);

  return (
    <OfficeTutorialContext.Provider
      value={{
        activeStep: [index, direction],
        tutorial,
        tutorialSteps,
        onNextSlide,
        onPrevSlide,
        setTutorial,
        onClose,
        openTutorial,
      }}
    >
      {children}
      <OfficeTutorialPopup />
    </OfficeTutorialContext.Provider>
  );
};
